import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Atom, ChevronLeft, ChevronRight, Droplet, ShieldCheck } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import { Reveal } from "@/components/pharmacy/hooks";
import { ManagedReviews, ManagedInstagram } from "@/components/storefront/HomeSections";
import ToneImg from "@/components/storefront/ToneImg";
import { useTone } from "@/lib/imageTone";
import { A, ATitle, AButton, AromaCard, label } from "@/components/aroma/AromaParts";
import { homeSets } from "@/lib/homesets";
import { inr } from "@/lib/storeutils";

const wrap = "max-w-6xl mx-auto px-5";

const BADGES = [["native", "Native — nature product"], ["natural", "Natural — herbs"], ["organic", "Natural product"], ["herbs", "Herbs — 100% pure"], ["aroma", "Aroma — herbs"], ["raw", "Raw — nature product"]];
const PERKS = [[ShieldCheck, "Skin Protection"], [Droplet, "Waterproof"], [Atom, "Antioxidant"]];

const uniq = (list) => { const seen = new Set(); return list.filter((p) => p && !seen.has(p.id) && seen.add(p.id)); };

export default function AromaHome() {
  const s = useStore();
  const cart = useCart();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  useEffect(() => { if (q && !s.loading) document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }, [q, s.loading]);

  const all = s.products;
  const sets = useMemo(() => homeSets(all, { fresh: 8, featured: 4, sale: 4 }), [all]);
  if (s.loading || !s.store) return <div className="min-h-screen flex items-center justify-center"><span className="w-10 h-10 rounded-full border-4 border-stone-200 border-t-stone-700 animate-spin" /></div>;
  if (s.error) return <div className="min-h-screen flex items-center justify-center text-slate-500">{s.error}</div>;

  const { slug, store } = s;
  const withImg = all.filter((p) => p.image);
  const lead = (all.filter((p) => p.featured && p.image).length ? all.filter((p) => p.featured && p.image) : withImg);
  const cats = s.categories.map((name) => ({ name, count: all.filter((p) => p.category === name).length, image: all.find((p) => p.category === name && p.image)?.image }));
  const shown = all.filter((p) => `${p.name} ${p.category || ""}`.toLowerCase().includes(q));
  const special = uniq([...sets.featured, ...sets.sale]).slice(0, 8);
  // "Selling fast" prefers products no shelf above has shown; it only repeats some when the catalogue is small
  const shownAbove = new Set([...sets.fresh, ...special].map((p) => p.id));
  const fresh = all.filter((p) => !shownAbove.has(p.id));
  const fast = uniq([...fresh, ...sets.sale, ...sets.featured]).slice(0, 10);
  const prices = all.map((p) => p.price).filter((n) => n > 0);

  if (q) {
    return (
      <StoreShell>
        <section id="shop" className={`${wrap} py-12`} data-testid="aroma-search-results">
          <ATitle eyebrow="Search">Results for “{q}”</ATitle>
          {shown.length === 0 ? <div className="text-center py-16" style={{ color: A.muted }}>No products found.</div> : (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="storefront-products">{shown.map((p) => <AromaCard key={p.id} p={p} s={s} cart={cart} />)}</div>
          )}
        </section>
      </StoreShell>
    );
  }

  return (
    <StoreShell>
      <Hero s={s} slides={lead.slice(0, 3)} />

      {/* Categories: four square photo tiles */}
      {cats.length > 0 && (
        <section id="categories" className={`${wrap} py-12 md:py-14`}>
          <div className={`grid gap-3 md:gap-6 mx-auto ${{ 1: "grid-cols-1 max-w-[260px]", 2: "grid-cols-2 max-w-xl", 3: "grid-cols-3 max-w-3xl" }[Math.min(cats.length, 4)] || "grid-cols-2 md:grid-cols-4"}`}>
            {cats.slice(0, 4).map((c, k) => (
              <Reveal key={c.name} delay={k * 90} kind="rv-zoom">
                <Link to={`/store/${slug}/shop?category=${encodeURIComponent(c.name)}`} data-testid={`aroma-cat-${c.name}`} className="group relative block aspect-square overflow-hidden" style={{ background: A.sand }}>
                  {c.image && <ToneImg src={c.image} loading="lazy" imgClassName="transition-transform duration-700 group-hover:scale-110" />}
                  <span className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(43,36,32,.45), rgba(43,36,32,0) 55%)" }} />
                  <span className="absolute left-0 right-0 bottom-5 text-center text-white text-[13px] sm:text-[17px] md:text-[19px] transition-transform duration-500 group-hover:-translate-y-1" style={{ fontFamily: A.serif, textShadow: "0 1px 8px rgba(0,0,0,.35)" }}>{c.name}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Experience The Purity: the quote and the six badges, exactly as in the design */}
      <section className="aroma-purity" data-testid="aroma-purity">
        <Reveal>
          <p>&quot;Experience The Purity Of Nature With Our Organic Soaps, Handcrafted<br className="hidden md:block" /> With Love To Nourish Your Skin And Protect The Planet.&quot;</p>
        </Reveal>
        <div className="aroma-badges">
          {BADGES.map(([name, alt], k) => (
            <Reveal key={name} delay={k * 80} kind="rv-zoom"><img src={`/aroma/badge-${name}.png`} alt={alt} loading="lazy" draggable={false} /></Reveal>
          ))}
        </div>
      </section>

      {/* Trending */}
      {sets.fresh.length > 0 && (
        <section id="new-arrivals" className={`${wrap} py-14 md:py-16`}>
          <Reveal><ATitle eyebrow="Nourish your skin naturally">Trending Products</ATitle></Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="storefront-products">
            {sets.fresh.map((p, k) => <Reveal key={p.id} delay={(k % 4) * 80} kind="rv-zoom"><AromaCard p={p} s={s} cart={cart} /></Reveal>)}
          </div>
          <div className="mt-10 text-center"><AButton to={`/store/${slug}/shop`}>View all products</AButton></div>
        </section>
      )}

      {/* Collections */}
      <Collections s={s} cats={cats} fallback={uniq([...sets.featured, ...sets.fresh])} />

      {/* Special products */}
      {special.length > 1 && (
        <section id="featured" className={`${wrap} py-14 md:py-16`}>
          <Reveal><ATitle eyebrow="Indulge in nature’s luxury">Special Products</ATitle></Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {special.map((p, k) => <Reveal key={p.id} delay={(k % 4) * 80} kind="rv-zoom"><AromaCard p={p} s={s} cart={cart} /></Reveal>)}
          </div>
        </section>
      )}

      {/* Promo banner */}
      <Promo s={s} product={sets.sale[0] || lead[1] || lead[0]} lo={prices.length ? Math.min(...prices) : 0} hi={prices.length ? Math.max(...prices) : 0} />

      {/* Selling fast carousel */}
      {fast.length > 1 && <SellingFast s={s} items={fast} cart={cart} />}

      {/* Vendor-managed sections */}
      <ManagedReviews s={s} layout="aroma" />
      <ManagedInstagram s={s} layout="aroma" />
    </StoreShell>
  );
}

/* ------------------------------------------------------------------ Hero */
function Hero({ s, slides }) {
  const { slug, store } = s;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => { if (paused || slides.length < 2) return undefined; const t = setInterval(() => setI((x) => (x + 1) % slides.length), 6000); return () => clearInterval(t); }, [paused, slides.length]);
  const cur = slides[i];
  const line = (cur?.description || "").split(/(?<=[.!?])\s/)[0];
  return (
    <section className="relative overflow-hidden" style={{ background: A.sand }} data-testid="aroma-hero" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="relative md:h-[540px] flex flex-col md:block">
        {/* behind the copy: a contrasting colour for cut-out (transparent) products, a soft blurred copy of the photo otherwise */}
        {slides.map((p, k) => <HeroBackdrop key={`bg-${p.id}`} src={p.image} on={k === i} />)}
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(246,241,234,.2), rgba(246,241,234,.9) 70%)" }} />
        <div className={`${wrap} relative md:h-full grid md:grid-cols-[1.15fr_1fr] items-center gap-6 md:gap-10 py-8 md:py-0`}>
          <div className="relative h-[280px] sm:h-[340px] md:h-[440px]">
            {slides.map((p, k) => (
              <Link key={p.id} to={`/store/${slug}/product/${p.id}`} aria-label={p.name} tabIndex={k === i ? 0 : -1} className="absolute inset-0 block transition-opacity duration-1000" style={{ opacity: k === i ? 1 : 0, pointerEvents: k === i ? "auto" : "none" }}>
                <HeroPhoto p={p} on={k === i} />
              </Link>
            ))}
          </div>
          <div key={i} className="aroma-hero-copy text-center md:text-left pb-6 md:pb-0">
            <div className="text-[11px] uppercase" style={{ ...label, color: A.ink }}>{store.business_category || "Handcrafted care"}</div>
            <h1 className="mt-3 text-[36px] md:text-[52px] leading-[1.05]" style={{ fontFamily: A.serif, color: A.ink }}>{store.tagline || "Be Aromatic"}</h1>
            <p className="mt-4 text-[13.5px] leading-relaxed max-w-sm mx-auto md:mx-0 line-clamp-2" style={{ color: "#5c5249" }}>{line || `Handcrafted care from ${store.name}, made with natural ingredients and delivered to your door.`}</p>
            <div className="mt-7"><AButton to={cur ? `/store/${slug}/product/${cur.id}` : `/store/${slug}/shop`} testId="aroma-hero-cta" solid>Shop now</AButton></div>
          </div>
        </div>
        {slides.length > 1 && (
          <div className="relative md:absolute left-0 right-0 bottom-4 flex justify-center gap-2 pb-4 md:pb-0">
            {slides.map((p, k) => <button key={p.id} type="button" aria-label={`Slide ${k + 1}`} onClick={() => setI(k)} className="h-1.5 rounded-full transition-all" style={{ width: k === i ? 26 : 8, background: k === i ? A.ink : "rgba(43,36,32,.3)" }} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function HeroBackdrop({ src, on }) {
  const tone = useTone(src);
  return tone
    ? <div aria-hidden className="absolute inset-0 transition-opacity duration-1000" style={{ background: tone.bg, opacity: on ? 1 : 0 }} />
    : <img src={src} alt="" aria-hidden data-tone-skip className="absolute inset-0 w-full h-full object-cover scale-110 transition-opacity duration-1000" style={{ filter: "blur(28px) saturate(1.1)", opacity: on ? 0.55 : 0 }} />;
}

function HeroPhoto({ p, on }) {
  const tone = useTone(p.image);
  return <img src={p.image} alt={p.name} data-tone-skip className="w-full h-full object-contain" style={{ transform: on ? "scale(1)" : "scale(.96)", transition: "transform 1.2s ease", filter: `drop-shadow(0 ${tone ? 22 : 18}px ${tone ? 26 : 28}px rgba(43,36,32,${tone ? 0.3 : 0.18}))` }} />;
}

/* ----------------------------------------------------------- Collections */
function Collections({ s, cats, fallback }) {
  const { slug } = s;
  // five collections: the store's categories, or its best products when it has fewer than three categories
  const useCats = cats.filter((c) => c.image).length >= 3;
  const items = useCats
    ? cats.filter((c) => c.image).slice(0, 5).map((c) => ({ key: c.name, name: c.name, image: c.image, to: `/store/${slug}/shop?category=${encodeURIComponent(c.name)}`, text: `${c.count} handpicked product${c.count === 1 ? "" : "s"}, crafted with care and natural ingredients.` }))
    : fallback.filter((p) => p.image).slice(0, 5).map((p) => ({ key: p.id, name: p.name, image: p.image, to: `/store/${slug}/product/${p.id}`, text: p.category ? `From our ${p.category} range — made with natural ingredients.` : "Made with natural ingredients and a lot of care." }));
  if (items.length < 3) return null;
  return (
    <section id="collection" style={{ background: A.cream }}>
      <div className={`${wrap} py-14 md:py-16`}>
        <Reveal><ATitle eyebrow="Discover the beauty of pure, natural ingredients">Our Natural Collection</ATitle></Reveal>
        <div className={`mt-10 grid grid-cols-2 gap-x-4 gap-y-8 ${items.length === 5 ? "md:grid-cols-5" : items.length === 4 ? "md:grid-cols-4" : "md:grid-cols-3"} md:gap-x-6`}>
          {items.map((c, k) => (
            <Reveal key={c.key} delay={k * 90} kind="rv-zoom" className={items.length % 2 && k === items.length - 1 ? "col-span-2 md:col-span-1 max-w-[220px] mx-auto md:max-w-none w-full" : ""}>
              <Link to={c.to} data-testid={`aroma-collection-${k}`} className="group block text-center">
                <span className="block aspect-[4/3] overflow-hidden" style={{ background: A.sand }}><ToneImg src={c.image} loading="lazy" imgClassName="transition-transform duration-700 group-hover:scale-110" /></span>
                <span className="block mt-4 text-[14.5px]" style={{ fontFamily: A.serif, color: A.ink }}>{c.name}</span>
                <span className="block mt-1.5 text-[11.5px] leading-relaxed px-1" style={{ color: A.muted }}>{c.text}</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- Promo */
function Promo({ s, product, lo, hi }) {
  const { slug, store } = s;
  return (
    <section id="deals" style={{ background: A.sand }} data-testid="aroma-promo">
      <div className={`${wrap} py-12 md:py-0 grid md:grid-cols-2 gap-8 items-center md:min-h-[400px]`}>
        <Reveal kind="rv-left" className="text-center md:text-left">
          <div className="text-[11px] uppercase" style={{ ...label, color: A.muted }}>Best for you</div>
          <h2 className="mt-3 text-[30px] md:text-[40px] leading-[1.15]" style={{ fontFamily: A.serif, color: A.ink }}>Pure Earth Elements:<br />A Natural Soap Odyssey</h2>
          {lo > 0 && <div className="mt-4 text-[20px]" style={{ fontFamily: A.serif, color: A.accent }}>{lo === hi ? inr(lo) : `${inr(lo)} – ${inr(hi)}`}</div>}
          <div className="mt-6 flex justify-center md:justify-start gap-6 md:gap-8">
            {PERKS.map(([I, t]) => (
              <div key={t} className="flex flex-col items-center gap-2 w-[86px] text-center"><I size={26} strokeWidth={1.25} style={{ color: A.ink }} /><span className="text-[11px] leading-tight" style={{ color: A.ink }}>{t}</span></div>
            ))}
          </div>
          <div className="mt-8"><AButton to={product ? `/store/${slug}/product/${product.id}` : `/store/${slug}/shop`}>Shop now</AButton></div>
        </Reveal>
        <Reveal kind="rv-right" className="relative md:self-stretch min-h-[280px]">
          {product?.image && (
            <Link to={`/store/${slug}/product/${product.id}`} className="block md:absolute md:inset-y-0 md:-right-5 md:left-0 overflow-hidden aspect-[4/3] md:aspect-auto">
              <ToneImg src={product.image} alt={product.name} loading="lazy" imgClassName="rx-float-slow" pad="12%" />
            </Link>
          )}
        </Reveal>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------- Selling fast */
function SellingFast({ s, items, cart }) {
  const rail = useRef(null);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const measure = () => {
    const el = rail.current; if (!el) return;
    setPages(Math.max(1, Math.round(el.scrollWidth / el.clientWidth)));
    setPage(Math.round(el.scrollLeft / Math.max(el.clientWidth, 1)));
  };
  useEffect(() => { measure(); window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }, [items.length]);
  const go = (d) => { const el = rail.current; if (el) el.scrollBy({ left: d * el.clientWidth, behavior: "smooth" }); };
  return (
    <section id="bestsellers" className={`${wrap} py-14 md:py-16`}>
      <Reveal><ATitle eyebrow="Beauty products">Selling Fast</ATitle></Reveal>
      <div className="relative mt-10">
        <button type="button" onClick={() => go(-1)} aria-label="Previous products" className="hidden md:flex absolute -left-4 top-[34%] z-10 w-10 h-10 rounded-full bg-white items-center justify-center shadow-md transition-transform hover:scale-110" style={{ color: A.ink }}><ChevronLeft size={18} /></button>
        <button type="button" onClick={() => go(1)} aria-label="Next products" className="hidden md:flex absolute -right-4 top-[34%] z-10 w-10 h-10 rounded-full bg-white items-center justify-center shadow-md transition-transform hover:scale-110" style={{ color: A.ink }}><ChevronRight size={18} /></button>
        <div ref={rail} onScroll={measure} className="flex gap-4 md:gap-6 overflow-x-auto scroll-smooth" style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }} data-testid="aroma-rail">
          {items.map((p) => (
            <div key={p.id} className="shrink-0 w-[calc(50%-8px)] md:w-[calc(25%-18px)]" style={{ scrollSnapAlign: "start" }}><AromaCard p={p} s={s} cart={cart} /></div>
          ))}
        </div>
        {pages > 1 && (
          <div className="mt-7 flex justify-center gap-2">
            {Array.from({ length: pages }).map((_, k) => <button key={k} type="button" aria-label={`Page ${k + 1}`} onClick={() => rail.current?.scrollTo({ left: k * rail.current.clientWidth, behavior: "smooth" })} className="w-2 h-2 rounded-full transition-colors" style={{ background: k === page ? A.accent : "transparent", border: `1px solid ${A.accent}` }} />)}
          </div>
        )}
      </div>
    </section>
  );
}
